import { useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { ColumnDef } from '@tanstack/react-table'
import { DataTable, SortableHeader } from '@/components/ui/data-table'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { useQuery } from '@tanstack/react-query'
import { adminQueries } from '@/services/adminApi'
import { AdminTransaction } from '@/services/adminApi'
import { TRANSACTION_STATUS_LABELS, TRANSACTION_TYPE_LABELS, getStatusBadgeVariant } from '@/types/account'
import { CurrencyDisplay } from '@/components/ui/currency-display'

const columns: ColumnDef<AdminTransaction, any>[] = [
  {
    accessorKey: 'id',
    header: ({ column }) => <SortableHeader column={column} label="ID" />,
  },
  {
    id: 'user',
    header: 'User',
    cell: ({ row }) => {
      const accountId = row.original.accountId
      const accountEmail = row.original.accountEmail
      if (!accountId) return '-'
      return (
        <Link
          to="/admin/users/$accountId"
          params={{ accountId }}
          className="text-blue-600 hover:underline"
        >
          {accountEmail || accountId}
        </Link>
      )
    },
  },
  {
    accessorKey: 'externalId',
    header: ({ column }) => <SortableHeader column={column} label="External ID" />,
    cell: ({ row }) => {
      const externalId = row.original.externalId
      if (!externalId) return '-'
      return (
        <span className="text-xs font-mono text-muted-foreground">
          {externalId}
        </span>
      )
    },
  },
  {
    accessorKey: 'type',
    header: ({ column }) => <SortableHeader column={column} label="Type" />,
    cell: ({ row }) => {
      const type = row.original.type
      const label = TRANSACTION_TYPE_LABELS[type] || type
      return <Badge variant="outline">{label}</Badge>
    },
  },
  {
    id: 'amount',
    header: 'Amount',
    cell: ({ row }) => {
      const payment = row.original.payment
      if (!payment) return '-'
      return <CurrencyDisplay amount={payment.amount} currency={payment.currency} inCents />
    },
  },
  {
    accessorKey: 'status',
    header: ({ column }) => <SortableHeader column={column} label="Status" />,
    cell: ({ row }) => {
      const status = row.original.status
      const label = TRANSACTION_STATUS_LABELS[status] || status
      return <Badge variant={getStatusBadgeVariant(status)}>{label}</Badge>
    },
  },
  {
    accessorKey: 'createdAt',
    header: ({ column }) => <SortableHeader column={column} label="Created At" />,
    cell: ({ row }) => {
      const date = row.original.createdAt
      if (!date) return '-'
      return new Date(date).toLocaleString()
    },
  },
  {
    accessorKey: 'lastUpdatedAt',
    header: ({ column }) => <SortableHeader column={column} label="Updated At" />,
    cell: ({ row }) => {
      const date = row.original.lastUpdatedAt
      if (!date) return '-'
      return new Date(date).toLocaleString()
    },
  },
]

export const Route = createFileRoute('/admin/transactions')({
  component: AdminTransactionsPage,
})

function AdminTransactionsPage() {
  const [page, setPage] = useState(1)
  const pageSize = 20
  const { data, isLoading } = useQuery(adminQueries.transactions(page, pageSize))

  return (
    <div className="space-y-4">
      <h1 className="text-3xl font-bold">Transactions</h1>
      <Card flush className="overflow-hidden">
        <CardHeader>
          <CardTitle>All Transactions</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-96 w-full" />
          ) : (
            <DataTable
              columns={columns}
              data={data?.transactions ?? []}
              enableSearch={false}
              manualPagination={{
                pageIndex: page,
                pageSize,
                totalItems: data?.totalItems ?? 0,
                onPageChange: setPage,
                isLoading,
              }}
            />
          )}
        </CardContent>
      </Card>
    </div>
  )
}
